import type { Scene } from '@babylonjs/core/scene';

import type { WeaponClass } from '../../net/protocol';
import { type Cell, WEAPON } from './atlas';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';

import type { CellLighting, MaterialName, ModelPalette } from './palette';
import { MIN_OUTLINE_WIDTH } from '../pixelate';
import {
  box,
  capsule,
  cylinder,
  type HoldKind,
  type HoldOffsets,
  lathe,
  type Rig,
  sphere,
  torus,
  tube,
  wedge,
} from './rig';

/**
 * The ten weapon silhouettes, one per `weapon_type` in the catalogues.
 *
 * **Cosmetic only, and saying so is part of shipping it.** A weapon's damage, speed
 * and crit come from its `base_stats`; the silhouette is decided by its *type*, which
 * every weapon in `items.yaml` and `uniques.yaml` already carries. Nothing here reads
 * a number the simulation reads, and nothing the simulation reads is derived from a
 * shape — the same rule the armour classes follow in `ARMOR-VISUALS.md`.
 *
 * **Proportion, length and mass — not detail.** A character is roughly 30 × 45 device
 * pixels at the gameplay camera and the pixelate post-process throws away anything
 * finer, so what separates a hatchet from a cudgel is the reach and where the weight
 * sits, never a bevel. `weapons.test.ts` checks these numbers; `silhouette.test.ts`
 * measures the rigs they build and asserts each of the 45 pairs differs by more than
 * two output pixels in *some* axis, because the numbers are not the shape.
 *
 * **One-handers hang, two-handers ride the shoulder**, and that is the strongest
 * distinction in the set: `above` — how far the weapon rises over the hand — separates
 * every one-handed silhouette from every two-handed one by more than half a unit on
 * its own. It is also the only arrangement that keeps a 2.4-unit staff out of the
 * ground; a greatsword pointed down would put its tip a unit below the floor.
 *
 * **A two-hander blocks the off-hand; it does not occupy it.** `twoHanded` is read by
 * `buildCrusader`, which then draws no kite shield. Nothing stores a second copy of
 * the weapon anywhere, and the server sends one field: the block is *derived* from
 * the main hand, exactly as `item.Base.TwoHanded()` derives it server-side.
 *
 * The profiles are plain values so they are testable without a scene — the split
 * `poses.ts` and `armour.ts` have. Only `buildWeapon` touches Babylon.
 */
export interface WeaponProfile {
  /**
   * How the striking part is put together.
   *
   *  - `blade`  — a flat blade centred on the axis, with a crossguard (swords, dagger)
   *  - `bit`    — an axe head offset to one side of the haft
   *  - `flange` — a head square in plan: a mace, and a staff's knob
   *  - `stave`  — two limbs bowed forward with a string across them (bow)
   */
  stance: 'blade' | 'bit' | 'flange' | 'stave';
  /**
   * How the weapon sits in the hand, as a rotation about the hand's X axis, in radians.
   *
   * **Zero is straight down the forearm, which is how everything used to be held**, and
   * it is wrong for almost every weapon: a hand closes *across* a hilt, so a sword's
   * blade runs roughly perpendicular to the arm rather than in line with it. A longsword
   * held at zero reads as a torch pointed at the floor.
   *
   * Negative tips the head *forward*, away from the body. Applied once to `rig.grip` when
   * the weapon is built; the pose's `weaponPitch` composes on top, so a swing still
   * swings from wherever the grip put it.
   *
   * **It is not the same for everything, and the exceptions are not laziness.** A
   * two-hander rides the shoulder and a bow is held with its limbs vertical — both are
   * how those weapons are actually carried, and both are *measured* rules in
   * `silhouette.test.ts`: two-handers must sit above the hand, and nothing may bury more
   * blade than the shipped longsword. Rotating them flat broke both, and the tests were
   * right. So the grip is a one-handed thing, which is precisely the case the change was
   * about: a hand closing across a hilt.
   */
  gripPitch: number;
  /** Grip or haft: width, length, depth. */
  shaft: readonly [number, number, number];
  /** Centre of the shaft along the weapon's axis. Positive is above the hand. */
  shaftY: number;
  /** The striking part: width, length, depth. For a bow, one limb. */
  head: readonly [number, number, number];
  /** Centre of the striking part along the axis. */
  headY: number;
  /** How far the head sits to one side of the haft. Only read by `bit`. */
  headX: number;
  /** Crossguard — width, length, depth — or null for anything hafted. */
  guard: readonly [number, number, number] | null;
  /** Centre of the crossguard along the axis. */
  guardY: number;
  /** Pommel or counterweight, or null. */
  butt: readonly [number, number, number] | null;
  /** Centre of the pommel along the axis. */
  buttY: number;
  /** Both hands, so no kite shield is drawn on the off arm. */
  twoHanded: boolean;
  /** Material of the grip or haft. */
  shaftMaterial: MaterialName;
  /** Material of the striking part, which is what carries the weapon's colour. */
  headMaterial: MaterialName;
  /**
   * Painted surfaces from the weapon sheet, over `shaftMaterial`/`headMaterial`.
   *
   * The sheet's first two rows are close-ups painted on a **diagonal** — a blade edge running
   * corner to corner, a shaft crossing the cell — which is why they suit a long thin part: the
   * grain runs along the thing rather than across it. The materials stay as the fallback and as
   * the source of the emissive lift, exactly as they do for armour.
   */
  shaftCell?: Cell;
  headCell?: Cell;
  /** `selfLit` for a head painted *as* light — a crystal, an orb. */
  headLighting?: CellLighting;
  /**
   * What the `flange` head is turned into. Phase 41 — the box head became a shape:
   *
   *  - `knob`      — a faceted ball on a collar: a mace, a maul (default)
   *  - `crystal`   — a six-sided bipyramid: the wand's and the staff's lit stone
   *  - `doublebit` — two axe bits mirrored across the haft: the greataxe
   */
  headShape?: 'knob' | 'crystal' | 'doublebit';
  /**
   * How the arms and the wrists sit while carrying this. Added to every pose by `applyPose`, so a
   * weapon's carry holds in idle, walk and cast alike without any pose knowing what is held.
   * Absent means the hands hang free. The off arm's numbers are unused where `offHandGrip`
   * is set: that arm is solved onto the grip instead.
   */
  hold?: Partial<HoldOffsets>;
  /**
   * The stretch of the grip the **second hand** may close on, as `[from, to]` in Y along the grip
   * (the main hand is at 0; the head is +Y). Present means the off arm is **solved every frame**
   * to the point of that stretch nearest its shoulder (`reachOffHand` in `rig.ts`) instead of
   * posed, and `hold`'s off-arm numbers are unused. A stretch rather than a point because a fixed
   * point was out of reach: 0.67–0.83 from the left shoulder against an arm of 0.48. Chosen, per
   * weapon: the hilt below the main hand on a greatsword, the haft above it on an axe, a maul and
   * a staff.
   */
  offHandGrip?: readonly [number, number];
  /** A bow is drawn, not swung. Absent means `swing`. */
  holdKind?: HoldKind;
}

/**
 * The longsword the crusader has always carried.
 *
 * This is the fallback for an empty main hand and for a weapon type this client has
 * never heard of, and it is deliberately a *weapon* rather than an empty hand: there
 * is no bare-fisted look to design, an unarmed character still swings and still deals
 * the game's base 15 damage, and a fresh player should not appear to have lost
 * something — the same reasoning that gives a bare chest the body it always had.
 *
 * It is longer than `SWORD_1H`'s blade and has the original bronze guard, so it reads
 * as the model this project shipped with rather than as a shortsword.
 */
const UNARMED: WeaponProfile = {
  stance: 'blade',
  // A hilt or haft is gripped across the palm, so the weapon runs out perpendicular
    // to the forearm rather than hanging down it.
  gripPitch: -Math.PI / 2,
  shaft: [0.07, 0.18, 0.07],
  shaftY: -0.06,
  head: [0.1, 0.96, 0.04],
  headY: -0.66,
  headX: 0,
  guard: [0.3, 0.06, 0.09],
  guardY: -0.16,
  butt: [0.1, 0.1, 0.1],
  buttY: 0.04,
  twoHanded: false,
  shaftMaterial: 'cloth',
  headMaterial: 'steel',
  shaftCell: WEAPON.leatherGrip,
  headCell: WEAPON.steelEdge,
};

/**
 * How a shouldered two-hander is carried — the greatsword, the greataxe, the war maul.
 *
 * **The blade must not run back through the arm.** With `gripPitch: 0` the weapon's axis lies
 * along the forearm and its head points *toward the elbow*, so a shouldered blade ran straight
 * up through the forearm and the upper arm. The single-segment box arm hid that — the 0.13-wide
 * blade sat inside a 0.17-wide cylinder and only showed above the shoulder — and the elbow of
 * Phase 41 exposed it: bend the forearm forward and the blade pointing back along it comes out
 * through the joint. Reported from one look: *the sword up towards its shoulder is going
 * through his arm.*
 *
 * So the carry is the real one. The forearm comes up (`elbowR` 1.1, the hand at chest height,
 * slightly forward), and the grip tips the head *forward* by 0.7 rad from the forearm's line —
 * which, with the forearm at 1.1 rad, leaves the blade about 25° back of vertical: up over the
 * shoulder, clear of the arm. The right arm turns in (`armRYaw`) so the hands meet in the middle,
 * and the free hand is solved onto the haft (`offHandGrip`, `reachOffHand`) — the fixed reach it
 * replaced never arrived.
 *
 * `gripPitch` is positive here where the one-handers' is negative, and that is the same axis
 * convention the staff's re-grip found: a head at +Y and a head at −Y turn opposite ways under
 * the same rotation.
 */
const SHOULDER_GRIP = 0.7;
/** The greatsword in guard: blade upright in front. See `SWORD_2H`. */
const GREATSWORD_GRIP = 2.0;
const GREATSWORD_HOLD: Partial<HoldOffsets> = { armRPitch: 0.3, armRYaw: 0.9, elbowR: 0.85 };
const SHOULDER_HOLD: Partial<HoldOffsets> = {
  armRPitch: 0.15,
  armRYaw: 0.9,
  elbowR: 1.1,
  armLPitch: 0.55,
  armLYaw: -0.6,
  elbowL: 0.8,
};

/**
 * One profile per weapon type.
 *
 * The declared order is the ladder of reach, shortest first, because that is the
 * dimension a player reads at a glance and the one most of the 45 pairs are separated
 * on. Where two weapons sit at the same reach they are separated across the haft
 * instead — a hatchet's bit against a shortsword's guard — or in depth: a mace head is
 * square in plan and a blade is not.
 */
const PROFILES: Record<WeaponClass, WeaponProfile> = {
  // ── One-handed: hang from the hand, tip toward the floor ──────────────────
  //
  // A dagger is the shortest thing in the game: a wrapped grip, a token guard and a
  // knapped blade. Small on purpose — the guard is what stops it reading as a bare
  // fist at 9 device pixels.
  DAGGER: {
    stance: 'blade',
    // A hilt or haft is gripped across the palm, so the weapon runs out perpendicular
    // to the forearm rather than hanging down it.
    gripPitch: -Math.PI / 2,
    shaft: [0.06, 0.12, 0.06],
    shaftY: -0.04,
    head: [0.08, 0.2, 0.03],
    headY: -0.24,
    headX: 0,
    guard: [0.14, 0.04, 0.07],
    guardY: -0.11,
    butt: null,
    buttY: 0,
    twoHanded: false,
    shaftMaterial: 'cloth',
    headMaterial: 'bone',
    shaftCell: WEAPON.leatherGrip,
    headCell: WEAPON.steelEdge,
  },
  // A slim wooden shaft ending in the only lit crystal in the set. The wand is told
  // from the dagger by reach and from everything else by being the smallest thing
  // that glows.
  WAND: {
    stance: 'flange',
    // A hilt or haft is gripped across the palm, so the weapon runs out perpendicular
    // to the forearm rather than hanging down it.
    gripPitch: -Math.PI / 2,
    shaft: [0.05, 0.5, 0.05],
    shaftY: -0.2,
    head: [0.18, 0.18, 0.18],
    headY: -0.52,
    headX: 0,
    guard: null,
    guardY: 0,
    butt: null,
    buttY: 0,
    twoHanded: false,
    shaftMaterial: 'darkSteel',
    headMaterial: 'ember',
    shaftCell: WEAPON.ebonyShaft,
    headCell: WEAPON.rubyCore,
    headLighting: 'selfLit',
    headShape: 'crystal',
  },
  // A cudgel: short, and all of its mass in a head that is as deep as it is wide.
  // That squareness in plan is what tells it from a blade of the same length, and it
  // is measured rather than asserted — see silhouette.test.ts.
  MACE_1H: {
    stance: 'flange',
    // A hilt or haft is gripped across the palm, so the weapon runs out perpendicular
    // to the forearm rather than hanging down it.
    gripPitch: -Math.PI / 2,
    shaft: [0.08, 0.62, 0.08],
    shaftY: -0.27,
    head: [0.4, 0.3, 0.36],
    headY: -0.7,
    headX: 0,
    guard: null,
    guardY: 0,
    butt: [0.1, 0.08, 0.1],
    buttY: 0.06,
    twoHanded: false,
    shaftMaterial: 'darkSteel',
    headMaterial: 'steel',
    shaftCell: WEAPON.leatherGrip,
    headCell: WEAPON.rustedIron,
  },
  // The shortsword. A narrow guard, because the hatchet beside it needs the width to
  // itself: at the same reach those two are separated across the haft and nowhere
  // else.
  SWORD_1H: {
    stance: 'blade',
    // A hilt or haft is gripped across the palm, so the weapon runs out perpendicular
    // to the forearm rather than hanging down it.
    gripPitch: -Math.PI / 2,
    shaft: [0.07, 0.2, 0.07],
    shaftY: -0.07,
    head: [0.1, 0.92, 0.04],
    headY: -0.68,
    headX: 0,
    guard: [0.22, 0.06, 0.09],
    guardY: -0.18,
    butt: [0.1, 0.1, 0.1],
    buttY: 0.05,
    twoHanded: false,
    shaftMaterial: 'cloth',
    headMaterial: 'steel',
    shaftCell: WEAPON.leatherGrip,
    headCell: WEAPON.steelEdge,
  },
  // A bearded hatchet. The bit hangs off one side of the haft and outward from the
  // body, so the outline is lopsided — which is the whole of "that is an axe" at this
  // scale. Generously wide for the same reason.
  AXE_1H: {
    stance: 'bit',
    // A hilt or haft is gripped across the palm, so the weapon runs out perpendicular
    // to the forearm rather than hanging down it.
    gripPitch: -Math.PI / 2,
    shaft: [0.07, 1.2, 0.07],
    shaftY: -0.55,
    head: [0.52, 0.34, 0.14],
    headY: -0.94,
    headX: -0.24,
    guard: null,
    guardY: 0,
    butt: [0.09, 0.09, 0.09],
    buttY: 0.07,
    twoHanded: false,
    shaftMaterial: 'rust',
    headMaterial: 'steel',
    shaftCell: WEAPON.oakShaft,
    headCell: WEAPON.rustedIron,
  },

  // ── Two-handed: ride the shoulder, tip above the head ─────────────────────
  //
  // A bow is the one silhouette that is neither hung nor shouldered: it stands
  // vertically through the hand, its limbs bowed forward, with a pale string drawn
  // across them. Narrowest thing in the set and the only one with anything behind the
  // hand.
  BOW: {
    stance: 'stave',
    // A two-hander rides the shoulder and a bow's limbs stay vertical: both are how the
    // weapon is really carried, and both are measured rules in silhouette.test.ts.
    gripPitch: 0,
    shaft: [0.08, 0.26, 0.1],
    shaftY: 0,
    head: [0.06, 0.66, 0.06],
    headY: 0.44,
    headX: 0,
    guard: null,
    guardY: 0,
    butt: null,
    buttY: 0,
    twoHanded: true,
    shaftMaterial: 'rust',
    headMaterial: 'bone',
    // The bow's grip is wrapped leather and its limbs are pale ash — the one weapon whose
    // 'head' cell dresses two mirrored limbs rather than a striking end.
    shaftCell: WEAPON.leatherGrip,
    headCell: WEAPON.ashBow,
    // Drawn, never swung — `drawPose` plays for the basic attack instead of the chop.
    holdKind: 'draw',
  },
  // A warhammer. The deepest head in the game — deeper than it is wide — which is
  // what separates it from the greataxe at almost the same reach.
  MACE_2H: {
    stance: 'flange',
    gripPitch: SHOULDER_GRIP,
    shaft: [0.09, 1.36, 0.09],
    shaftY: 0.42,
    head: [0.36, 0.34, 0.44],
    headY: 0.98,
    headX: 0,
    guard: null,
    guardY: 0,
    butt: [0.1, 0.1, 0.1],
    buttY: -0.32,
    twoHanded: true,
    shaftMaterial: 'darkSteel',
    headMaterial: 'steel',
    shaftCell: WEAPON.oakShaft,
    headCell: WEAPON.rustedIron,
    // Carried over the shoulder: the forearm comes up so the hand is at the chest, and the
    // grip tips the blade back to rest above the shoulder — see `SHOULDER_GRIP`. The free
    // hand is solved onto the haft. See `WeaponProfile.offHandGrip`.
    hold: SHOULDER_HOLD,
    offHandGrip: [-0.2, 0.6],
  },
  // A greataxe. A double bit centred on the haft rather than offset — a head this
  // wide hanging off one side would swing through the character's own head.
  AXE_2H: {
    stance: 'flange',
    gripPitch: SHOULDER_GRIP,
    shaft: [0.09, 1.52, 0.09],
    shaftY: 0.5,
    // 0.70, not 0.62: carried over the shoulder at the same tilt as the greatsword, width is
    // what separates the two, and 0.62 left them 1.5 output pixels apart.
    head: [0.7, 0.44, 0.16],
    headY: 1.02,
    headX: 0,
    guard: null,
    guardY: 0,
    butt: [0.1, 0.1, 0.1],
    buttY: -0.32,
    twoHanded: true,
    shaftMaterial: 'rust',
    headMaterial: 'steel',
    shaftCell: WEAPON.oakShaft,
    headCell: WEAPON.steelEdge,
    headShape: 'doublebit',
    // Carried over the shoulder: the forearm comes up so the hand is at the chest, and the
    // grip tips the blade back to rest above the shoulder — see `SHOULDER_GRIP`. The free
    // hand is solved onto the haft. See `WeaponProfile.offHandGrip`.
    hold: SHOULDER_HOLD,
    offHandGrip: [-0.2, 0.6],
  },
  // The greatsword: the longest blade, with the widest crossguard, carried point-up.
  SWORD_2H: {
    stance: 'blade',
    gripPitch: GREATSWORD_GRIP,
    shaft: [0.08, 0.34, 0.08],
    shaftY: -0.12,
    head: [0.13, 1.52, 0.05],
    headY: 0.9,
    headX: 0,
    guard: [0.44, 0.08, 0.11],
    guardY: 0.08,
    butt: [0.12, 0.12, 0.12],
    buttY: -0.24,
    twoHanded: true,
    shaftMaterial: 'cloth',
    headMaterial: 'steel',
    shaftCell: WEAPON.leatherGrip,
    headCell: WEAPON.steelEdge,
    // **Held in guard**, upright in front with both hands on the hilt (2026-09-28). Shouldered,
    // its blade leant back past the head — reported: *not sure about the way we are holding the
    // two handed sword*. The grip and the hold are chosen by looking.
    hold: GREATSWORD_HOLD,
    offHandGrip: [-0.24, -0.1],
  },
  // The longest reach in the game, and the only weapon whose tip clears the
  // character's own head by more than half a unit. A lit knob so it reads as a
  // caster's staff rather than as a pole.
  STAFF: {
    stance: 'flange',
    // **Gripped across the palm like a sword, which is the one two-hander that does.**
    //
    // Reported: *the staff orientation is not good when in the hand, it should be perpendicular
    // like a sword.* Every other two-hander keeps `gripPitch: 0` — riding the shoulder along the
    // forearm — because that is how a greatsword and a war maul are really carried and it is what
    // keeps their tips out of the ground. A staff is not carried that way: it is *held*, across
    // the hand, and at 0 it lay along the arm and swung like a bat.
    //
    // It costs the shoulder-ride rule its universality, so `silhouette.test.ts` now states the
    // exception rather than asserting all five. What it does **not** cost is the floor: the shaft
    // is centred 0.4 above the hand, so tipping it forward puts the butt out in front of the
    // character rather than through the ground — measured, not assumed.
    //
    // **`+π/2`, not `−π/2`** (second report: *the top of the staff, the rounded part, should be in
    // front of the character not behind — it is the wrong orientation, 180 degrees*). The first
    // re-grip copied the one-handers' sign, and a one-hander's geometry runs the other way along
    // its own axis: `headY` is *negative* on every blade (the head hangs below the grip) and
    // **positive** on the staff (`+1.57`, the crystal rides above the hand). So the same rotation
    // that throws a blade forward throws the staff's crystal backwards. The sign is the axis
    // convention showing through, which is why it could not be got right by matching a sibling.
    gripPitch: Math.PI / 2,
    shaft: [0.09, 2.2, 0.09],
    shaftY: 0.4,
    head: [0.22, 0.22, 0.22],
    headY: 1.57,
    headX: 0,
    guard: null,
    guardY: 0,
    butt: null,
    buttY: 0,
    twoHanded: true,
    shaftMaterial: 'rust',
    headMaterial: 'ember',
    shaftCell: WEAPON.oakShaft,
    // The staff's crystal is the brightest thing a caster carries, so it is the painting
    // rather than the light: `selfLit` means the sun cannot decide how bright it is.
    headCell: WEAPON.rubyCore,
    headLighting: 'selfLit',
    headShape: 'crystal',
    // **One hand, and the other hangs free** (2026-09-28). It used to reach the off hand
    // "forward and across" to the shaft by fixed angles, and never arrived; brought to the middle
    // like the other two-handers, a staff gripped across the palm sticks out sideways like a
    // vaulting pole. Carried in the one hand as the owner asked (*perpendicular like a sword*).
    hold: {},
  },
};

/**
 * Every weapon class a profile exists for, for tests and for iteration.
 *
 * Held to `config.WeaponTypes` in both directions by
 * `grind/internal/item/weapon_mesh_test.go`, the same arrangement `mobs.yaml`'s mesh
 * ids and `procedural.ts`'s builder table have: a type the catalogue can name and
 * this client cannot draw would silently get the fallback longsword, and a class here
 * that no catalogue entry can produce is dead code.
 */
export const WEAPON_CLASSES: readonly WeaponClass[] = [
  'DAGGER',
  'WAND',
  'MACE_1H',
  'SWORD_1H',
  'AXE_1H',
  'BOW',
  'MACE_2H',
  'AXE_2H',
  'SWORD_2H',
  'STAFF',
];

/**
 * The profile for a wire value.
 *
 * Anything unrecognised falls back to the longsword rather than throwing or drawing
 * nothing: the server sends no `weapon` field for an empty main hand, and a newer
 * server naming an eleventh type must degrade to a weapon rather than to an
 * empty-handed player. A catalogue entry ahead of its model should look *wrong*, not
 * crash the render loop — the rule an unknown mob mesh id follows.
 */
export function weaponProfile(weapon?: string | null): WeaponProfile {
  if (!weapon) return UNARMED;
  return PROFILES[weapon as WeaponClass] ?? UNARMED;
}

/** The bare-handed profile, exported so a test can assert the fallback is it. */
export const UNARMED_PROFILE = UNARMED;

/**
 * How far a string sits behind a bow's grip, and how thick it is.
 *
 * The thickness clears `MIN_OUTLINE_WIDTH` deliberately: a string thinner than one
 * output pixel is averaged away by the pixelate post-process and nothing reports it,
 * which is exactly how the hover outline came to be invisible while being set on every
 * part as intended.
 */
const BOW_STRING_THICKNESS = 0.13;

/**
 * How far a bow's limb roots sit forward of the grip, and how far the limbs sweep back
 * from there, in radians.
 *
 * Together these decide the **brace height** — the gap between the string and the hand —
 * and that gap is the whole shape of a bow. It used to be *negative*: the limbs leaned back
 * by 0.3 rad from roots 0.1 forward, which put the tips at z ≈ 0, and the string was pinned
 * at a hand-written −0.06 with a thickness of 0.13. So the string box spanned −0.125 to
 * +0.005 and the grip box −0.05 to +0.05, and the two **overlapped**: the string ran
 * through the hand holding it. Reported directly — *we hold a bow in the middle shaft,
 * parallel to the arc, away from the string*.
 *
 * The lean is what fixes it, not a bigger offset: sweeping the tips further back is what a
 * braced bow actually does, where simply moving the string back would leave it floating
 * clear of the limbs it is supposed to be tied to.
 */
const BOW_LIMB_FORWARD = 0;
const BOW_LIMB_LEAN = 0.85;

/**
 * The floor the gap has to clear: **one output pixel**.
 *
 * A gap narrower than a pixel is not a gap — the pixelate post-process averages the string
 * and the grip into each other, and the string reads as running through the hand again for
 * a different reason. The same measurement `MIN_OUTLINE_WIDTH` exists for, applied to a
 * negative space rather than to a rim.
 *
 * At 0.85 rad the gap is about 1.1 output pixels and the bow stands 1.32 units — a brace
 * height of roughly a fifth of its height, against about a tenth on a real bow. Deeper than
 * life on purpose: at 25 device pixels to the unit, life is invisible.
 */
export const MIN_BRACE_GAP = MIN_OUTLINE_WIDTH;

/**
 * Where a bow's limb tips end up, given its profile.
 *
 * **Derived, never authored.** The string is tied to the tips, so a hand-written string
 * position is a second copy of a number the limbs already decide — and when the two
 * disagree the string floats off the bow, or through the hand, which is exactly what
 * happened. Pure, so `weapons.test.ts` can hold the brace height without a scene.
 */
export function bowTip(profile: WeaponProfile): { y: number; z: number } {
  const halfLimb = profile.head[1] / 2;
  return {
    y: profile.headY + halfLimb * Math.cos(BOW_LIMB_LEAN),
    z: BOW_LIMB_FORWARD - halfLimb * Math.sin(BOW_LIMB_LEAN),
  };
}

/**
 * The gap between the back of the grip and the front of the string, in world units.
 *
 * A real bow's brace height is the distance from the string to the deepest part of the
 * grip, and it is what keeps the string off the archer's forearm. Positive here means
 * *there is a gap*; zero or less means the string is drawn through the hand.
 */
export function bowBraceGap(profile: WeaponProfile): number {
  const gripBack = profile.shaftY - profile.shaft[2] / 2;
  const stringFront = bowTip(profile).z + BOW_STRING_THICKNESS / 2;
  return gripBack - stringFront;
}

/**
 * Builds a weapon into `rig.heldParts`.
 *
 * Called once at construction. **There is no runtime swap** — `entities.ts` rebuilds
 * the whole model when the snapshot's `weapon` changes, which happens on an equip and
 * nowhere else, exactly as it does for a chest piece. Pre-building all ten and
 * toggling `isVisible` would be worse than useless here: hidden meshes still have
 * bounds, so a character would carry the union of every weapon in the game.
 *
 * Everything is parented to `rig.weapon`, whose `rotation.x` the attack pose drives,
 * so a swing moves whatever is in the hand without the pose knowing what it is.
 */
export function buildWeapon(
  scene: Scene,
  rig: Rig,
  palette: ModelPalette,
  profile: WeaponProfile,
): void {
  // **How the hand holds it**, applied once. The pose rotates `rig.weapon` every frame —
  // that is the swing — and this composes underneath it, so a blade starts perpendicular
  // to the forearm and swings from there. See `Rig.grip`.
  rig.grip.rotation.x = profile.gripPitch;
  // And how the *rest of the body* carries it: the free hand's reach, the attack's shape.
  Object.assign(rig.hold, profile.hold ?? {});
  rig.holdKind = profile.holdKind ?? 'swing';
  // The second hand's place on the grip, which `applyPose` reaches the off arm to every frame.
  if (profile.offHandGrip !== undefined) {
    const [from, to] = profile.offHandGrip;
    const at = new TransformNode(`${rig.grip.name}-offhand-grip`, scene);
    at.parent = rig.grip;
    at.position.set(0, from, 0);
    rig.offGrip = at;
    rig.offGripSpan = to - from;
  }

  buildWeaponParts(scene, rig.grip, rig.heldParts, palette, profile, 'wpn');
}

/**
 * The geometry of a weapon profile, under any node, with any name prefix.
 *
 * Split from `buildWeapon` so the off hand can hold a dagger built by the same code as the
 * main hand's (`offhand.ts`). The parts are named `<prefix>-shaft`, `<prefix>-head` and so
 * on; `silhouette.test.ts` measures the `wpn-` set and the bow's four by those names.
 *
 * **Phase 41 turned each box into the shape it was standing in for.** A blade is a tapered
 * diamond section with a point and a rounded crossguard; an axe bit flares to its edge; a
 * mace head is a faceted ball on a collar; a bow's limbs are curves; a staff ends in a
 * crystal. The bounding boxes stay close to the old boxes' — the profile numbers are still
 * the silhouette — which is why every pairwise distinguishability figure survived.
 */
export function buildWeaponParts(
  scene: Scene,
  parent: TransformNode,
  parts: Mesh[],
  palette: ModelPalette,
  profile: WeaponProfile,
  prefix: string,
): void {
  // A painted cell where the profile names one, the flat material otherwise — and the material
  // is still what a cell falls back to and where its emissive lift comes from.
  const shaft = profile.shaftCell
    ? palette.cell(profile.shaftCell, profile.shaftMaterial)
    : palette.get(profile.shaftMaterial);
  const head = profile.headCell
    ? palette.cell(profile.headCell, profile.headMaterial, profile.headLighting)
    : palette.get(profile.headMaterial);
  const trim = palette.get('bronze');
  const dark = palette.get('darkSteel');

  if (profile.stance === 'stave') {
    buildBow(scene, parent, parts, palette, profile, prefix, shaft, head);
    return;
  }

  // The grip or haft: round, as a thing gripped is.
  cylinder(scene, parent, parts, {
    name: `${prefix}-shaft`,
    height: profile.shaft[1],
    diameterTop: profile.shaft[0],
    diameterBottom: profile.shaft[2],
    at: [0, profile.shaftY, 0],
    material: shaft,
    tessellation: 8,
  });

  if (profile.guard) {
    // Quillons: a rounded bar across the blade rather than a brick.
    capsule(scene, parent, parts, {
      name: `${prefix}-guard`,
      height: profile.guard[0],
      radiusTop: profile.guard[1] * 0.7,
      radiusBottom: profile.guard[1] * 0.7,
      at: [0, profile.guardY, 0],
      rotation: [0, 0, Math.PI / 2],
      material: trim,
      tessellation: 6,
    });
  }

  // Which way the striking end points: away from the grip. A one-hander's head hangs below
  // it (negative `headY`), a two-hander's rides above.
  const away = profile.headY < 0 ? -1 : 1;

  switch (profile.stance) {
    case 'blade':
      // A tapered diamond section running out to a point.
      wedge(scene, parent, parts, {
        name: `${prefix}-head`,
        size: [profile.head[0], profile.head[1], profile.head[2]],
        taper: 0.12,
        at: [0, profile.headY, 0],
        rotation: [0, 0, away < 0 ? Math.PI : 0],
        material: head,
      });
      break;

    case 'bit': {
      // The eye on the haft, and a bit flaring outward to its edge. The offset is negative —
      // outward from the body, since the sword arm hangs at -x — so the lopsided outline
      // reads against the sky rather than against the character's own chest.
      box(scene, parent, parts, {
        name: `${prefix}-eye`,
        size: [0.1, profile.head[1] * 0.6, profile.head[2]],
        at: [0, profile.headY, 0],
        material: dark,
      });
      wedge(scene, parent, parts, {
        name: `${prefix}-head`,
        size: [profile.head[1], profile.head[0], profile.head[2]],
        taper: 1.5,
        at: [-profile.head[0] / 2, profile.headY, 0],
        rotation: [0, 0, Math.PI / 2],
        material: head,
      });
      break;
    }

    default: {
      const shape = profile.headShape ?? 'knob';
      if (shape === 'crystal') {
        // A six-sided bipyramid on a collar: the stone the light comes from.
        const r = profile.head[0] / 2;
        const h = profile.head[1];
        lathe(scene, parent, parts, {
          name: `${prefix}-head`,
          profile: [
            [0.01, 0],
            [r, h * 0.42],
            [0.01, h],
          ],
          at: [0, profile.headY - h / 2, 0],
          material: head,
          tessellation: 6,
        });
        torus(scene, parent, parts, {
          name: `${prefix}-collar`,
          diameter: profile.shaft[0] * 1.6,
          thickness: profile.shaft[0] * 0.6,
          at: [0, profile.headY - away * (h / 2 + 0.01), 0],
          material: trim,
          tessellation: 8,
        });
      } else if (shape === 'doublebit') {
        // Two bits mirrored across the haft, each flaring to its own edge.
        for (const sign of [-1, 1] as const) {
          wedge(scene, parent, parts, {
            name: `${prefix}-head${sign < 0 ? '' : 'B'}`,
            size: [profile.head[1], profile.head[0] / 2, profile.head[2]],
            taper: 1.5,
            at: [(sign * profile.head[0]) / 4, profile.headY, 0],
            rotation: [0, 0, (sign * Math.PI) / 2],
            material: head,
          });
        }
        box(scene, parent, parts, {
          name: `${prefix}-eye`,
          size: [0.12, profile.head[1] * 0.5, profile.head[2] * 1.1],
          at: [0, profile.headY, 0],
          material: dark,
        });
      } else {
        // A faceted ball — four segments, so it reads as flanged rather than smooth — over a
        // collar where the haft enters it.
        sphere(scene, parent, parts, {
          name: `${prefix}-head`,
          size: [profile.head[0], profile.head[1], profile.head[2]],
          at: [0, profile.headY, 0],
          material: head,
          segments: 4,
        });
        torus(scene, parent, parts, {
          name: `${prefix}-collar`,
          diameter: profile.shaft[0] * 1.8,
          thickness: profile.shaft[0] * 0.7,
          at: [0, profile.headY - away * profile.head[1] * 0.55, 0],
          material: trim,
          tessellation: 8,
        });
      }
    }
  }

  if (profile.butt) {
    // A pommel or a butt cap: a ball, which is what stops a hand sliding off.
    sphere(scene, parent, parts, {
      name: `${prefix}-butt`,
      size: [profile.butt[0], profile.butt[1], profile.butt[2]],
      at: [0, profile.buttY, 0],
      material: trim,
      segments: 4,
    });
  }
}

/**
 * The bow: a grip, two curved limbs and a string tied to their tips.
 *
 * The limbs are **tubes along a curve** that bulges forward from the grip and sweeps back
 * to the tip — the shape of a braced recurve, where two straight leaning boxes read as a
 * letter K. The tip is still `bowTip(profile)`, so the string is still tied to where the
 * limb ends and the brace-height tests still measure what they measured.
 */
function buildBow(
  scene: Scene,
  parent: TransformNode,
  parts: Mesh[],
  palette: ModelPalette,
  profile: WeaponProfile,
  prefix: string,
  shaft: ReturnType<ModelPalette['get']>,
  head: ReturnType<ModelPalette['get']>,
): void {
  // The grip: a wrapped cylinder, as long as the profile's shaft.
  cylinder(scene, parent, parts, {
    name: `${prefix}-grip`,
    height: profile.shaft[1],
    diameterTop: profile.shaft[0],
    diameterBottom: profile.shaft[0],
    at: [0, profile.shaftY, 0],
    material: shaft,
    tessellation: 8,
  });

  const tip = bowTip(profile);
  const root = profile.shaft[1] / 2 - 0.02;
  const radius = profile.head[0] / 2;
  for (const sign of [1, -1] as const) {
    // Six points along a quadratic curve from the grip to the tip. The control point sits
    // forward of both, which is what makes the limb belly out and then come back.
    const p0 = new Vector3(0, sign * root, 0.01);
    const p1 = new Vector3(0, sign * (root + (tip.y - root) * 0.55), 0.16);
    const p2 = new Vector3(0, sign * tip.y, tip.z);
    const path: Vector3[] = [];
    for (let i = 0; i <= 6; i++) {
      const t = i / 6;
      const a = (1 - t) * (1 - t);
      const b = 2 * (1 - t) * t;
      const c = t * t;
      path.push(
        new Vector3(
          0,
          a * p0.y + b * p1.y + c * p2.y,
          a * p0.z + b * p1.z + c * p2.z,
        ),
      );
    }
    tube(scene, parent, parts, {
      name: `${prefix}-limb${sign > 0 ? 'U' : 'D'}`,
      path,
      radius,
      material: head,
      tessellation: 6,
    });
  }
  // **Tied to the tips**, both in where it sits and in how long it is. Both come from
  // `bowTip`, so the string cannot drift off the limbs or into the hand the way it did
  // when its position was a constant of its own.
  box(scene, parent, parts, {
    name: `${prefix}-string`,
    size: [BOW_STRING_THICKNESS, tip.y * 2, BOW_STRING_THICKNESS],
    at: [0, 0, tip.z],
    material: palette.get('bone'),
  });
}
